import { motion } from 'framer-motion';
import { Code, Megaphone, Users, Target, Lightbulb, Rocket } from 'lucide-react';

export const About = () => {
  return (
    <div className="min-h-screen grain pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl sm:text-6xl font-bold mb-6 tracking-tight" data-testid="about-page-title">
            Sobre <span className="text-gradient">Nosotros</span>
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            Somos un equipo apasionado por transformar ideas en soluciones digitales excepcionales
          </p>
        </motion.div>
        
        <section className="mb-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="aspect-video glass rounded-2xl overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1758873268663-5a362616b5a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjcmVhdGl2ZSUyMG9mZmljZSUyMHRlYW18ZW58MHx8fHwxNzc0Mzg2NTA1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Build Brains Team"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="space-y-6"
            >
              <h2 className="text-4xl font-bold mb-4">Quiénes Somos</h2>
              <p className="text-white/70 leading-relaxed">
                Build Brains nació de la visión de unir dos mundos: el desarrollo de software de alta calidad y el marketing digital estratégico. Creemos que la tecnología sin estrategia es incompleta, y la estrategia sin tecnología no puede escalar.
              </p>
              <p className="text-white/70 leading-relaxed">
                Nuestro equipo combina desarrolladores expertos con especialistas en marketing digital, trabajando en conjunto para crear productos que no solo funcionan perfectamente, sino que también alcanzan a la audiencia correcta y generan resultados medibles.
              </p>
              <p className="text-white/70 leading-relaxed">
                Con años de experiencia en la industria, hemos ayudado a decenas de empresas a transformar sus ideas en realidades digitales exitosas. Desde startups hasta empresas establecidas, cada proyecto es una oportunidad para crear algo extraordinario.
              </p>
            </motion.div>
          </div>
        </section>
        
        <section className="mb-24" data-testid="mission-vision-section">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass rounded-3xl p-8"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#C6FF00]/10 flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-[#C6FF00]" />
              </div>
              <h3 className="text-3xl font-bold mb-4">Misión</h3>
              <p className="text-white/70 leading-relaxed">
                Empoderar a empresas y emprendedores con soluciones digitales innovadoras que impulsen su crecimiento. Creamos tecnología accesible, escalable y efectiva, combinada con estrategias de marketing que maximizan el impacto y el ROI.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass rounded-3xl p-8"
            >
              <div className="w-16 h-16 rounded-2xl bg-[#9D00FF]/10 flex items-center justify-center mb-6">
                <Lightbulb className="w-8 h-8 text-[#9D00FF]" />
              </div>
              <h3 className="text-3xl font-bold mb-4">Visión</h3>
              <p className="text-white/70 leading-relaxed">
                Ser el socio tecnológico de referencia para empresas que buscan transformación digital completa. Queremos ser reconocidos por nuestra excelencia técnica, creatividad en marketing y compromiso con el éxito de nuestros clientes.
              </p>
            </motion.div>
          </div>
        </section>
        
        <section className="mb-24" data-testid="values-section">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4">Nuestros Valores</h2>
            <p className="text-white/60 text-lg">Los principios que guían cada proyecto que emprendemos</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                icon: Code,
                title: 'Excelencia Técnica',
                description: 'Código limpio, arquitectura sólida y las mejores prácticas de la industria.',
                color: '#C6FF00'
              },
              {
                icon: Megaphone,
                title: 'Marketing Estratégico',
                description: 'Decisiones basadas en datos y estrategias que generan resultados medibles.',
                color: '#FFFFFF'
              },
              {
                icon: Users,
                title: 'Enfoque en el Cliente',
                description: 'Tu éxito es nuestro éxito. Cada proyecto se trata de resolver tus necesidades.',
                color: '#BAFF39'
              },
              {
                icon: Rocket,
                title: 'Innovación Continua',
                description: 'Siempre aprendiendo, siempre mejorando, siempre a la vanguardia.',
                color: '#C6FF00'
              }
            ].map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-2xl p-6 hover:border-white/20 transition-all duration-300"
                data-testid={`value-${index}`}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: `${value.color}10` }}
                >
                  <value.icon className="w-6 h-6" style={{ color: value.color }} />
                </div>
                <h4 className="text-lg font-bold mb-2">{value.title}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </section>
        
        <motion.section
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass rounded-3xl p-12 text-center"
          data-testid="cta-section"
        >
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">¿Listo para trabajar con nosotros?</h2>
          <p className="text-white/60 text-lg mb-8 max-w-2xl mx-auto">
            Hablemos sobre cómo podemos ayudarte a alcanzar tus objetivos digitales.
          </p>
          <a
            href="mailto:contact@buildbrains.com"
            className="inline-block px-8 py-4 rounded-full bg-[#C6FF00] text-black font-semibold hover:glow-primary transition-all duration-300"
            data-testid="contact-cta"
          >
            Contactar Ahora
          </a>
        </motion.section>
      </div>
    </div>
  );
};